"use client";

import React, { useState, useEffect } from "react";
import Navbar from "@/components/Navbar";
import Footer2 from "@/components/landing-page/Footer2";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Check, ImageIcon } from "lucide-react";

export default function CaseStudiesPage() {
  const [selectedCaseStudy, setSelectedCaseStudy] = useState<any | null>(null);

  useEffect(() => {
    if (selectedCaseStudy) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [selectedCaseStudy]);

  const caseStudies = [
    {
      client: "TitanTech Investments",
      industry: "Finance",
      title: "Complete Digital Transformation",
      tags: ["Client Work", "live"],
      summary: "How We Rebuilt TitanTech's Legacy Systems to Accelerate Trade Execution and Client Onboarding.",
      heroMetrics: "80% Faster Onboarding, 3x Trade Execution Speed, 100% Data Pipeline Automation.",
      clientDescription: "TitanTech Investments is a mid-sized wealth management firm handling high-net-worth portfolios and complex asset allocations.",
      problem: "TitanTech relied on legacy mainframes and manual data entry across fragmented systems. Client onboarding took up to 3 weeks due to compliance bottlenecks, and trade execution data was heavily delayed, preventing real-time portfolio optimization.",
      solution: "We orchestrated a complete digital transformation. We replaced manual compliance checks with an automated KYC/AML pipeline, integrated modern cloud architecture to handle real-time market data, and developed an AI-driven analytics dashboard for instant portfolio visibility.",
      features: [
        "Automated KYC/AML Onboarding Pipeline",
        "Real-Time Market Data Integration",
        "AI-Driven Portfolio Analytics Dashboard",
        "Secure Cloud Infrastructure Migration"
      ],
      techStack: ["AWS", "Python", "React", "Snowflake", "OpenAI"]
    },
    {
      client: "Luxsun Energy",
      industry: "Infrastructure",
      title: "Infrastructure Automation Excellence",
      tags: ["Client Work", "live"],
      summary: "How We Automated Grid Monitoring and Reduced Infrastructure Downtime by 45%.",
      heroMetrics: "45% Reduction in Downtime, Automated Maintenance Dispatch, Real-time Sensor Telemetry.",
      clientDescription: "Luxsun Energy operates a vast network of solar arrays and power distribution nodes across multiple regions.",
      problem: "Monitoring thousands of distributed energy nodes was done manually via outdated SCADA systems. Anomalies were often detected too late, leading to prolonged grid downtime and inefficient manual dispatching of maintenance crews.",
      solution: "We deployed an IoT sensor integration layer connected to a centralized AI predictive maintenance engine. The system now ingests real-time telemetry, automatically flags impending hardware failures, and dispatches maintenance tickets to the nearest available crew before an outage occurs.",
      features: [
        "IoT Telemetry Data Ingestion",
        "Predictive Maintenance AI Engine",
        "Automated Field Crew Dispatching",
        "Centralized Grid Health Dashboard"
      ],
      techStack: ["Node.js", "PostgreSQL", "TensorFlow", "AWS IoT Core"]
    },
    {
      client: "SVS Food",
      industry: "F&B",
      title: "Full Tech Infrastructure Build",
      tags: ["Client Work", "in-progress"],
      summary: "Full Tech Infrastructure Migration & Build for a Shark Tank Featured Brand",
      heroMetrics: "100% In-House Tech Ownership, Seamless Omnichannel Experience, Custom Mobile & Kiosk Apps.",
      clientDescription: "SVS Food is a fast-growing F&B brand featured on Shark Tank India, expanding aggressively across physical and digital channels.",
      problem: "SVS Food operated on a third-party platform that bundled their website, app, and backend. They had no ownership of the platform, could not deeply customize the digital experience, lacked control over data, and were blocked from scaling their unique operational roadmap.",
      solution: "We are architecting and building their complete in-house technology stack from scratch. This includes a brand-aligned custom website, a consumer-facing mobile app for ordering and loyalty, a self-service kiosk ordering system for physical locations, and a comprehensive backend ERP/CRM system.",
      features: [
        "Custom Brand-Aligned Website",
        "Native Mobile App for Ordering",
        "In-Store Self-Service Kiosk System",
        "Complete Backend ERP & CRM",
        "Omnichannel Cloud Infrastructure"
      ],
      techStack: ["Next.js", "React Native", "Node.js", "PostgreSQL", "AWS"]
    },
    {
      client: "BNB Web",
      industry: "Travel Tech",
      title: "Serverless Cloud Scaling",
      tags: ["Client Work", "live"],
      summary: "How We Migrated to a Serverless Architecture to Handle 10K+ Concurrent Users Flawlessly.",
      heroMetrics: "10K+ Concurrent Users Supported, 99.99% Uptime Achieved, 60% Reduction in Idle Server Costs.",
      clientDescription: "BNB Web is an online booking platform that experiences massive traffic spikes during holiday seasons.",
      problem: "The platform faced severe scalability bottlenecks during peak booking seasons. Their monolithic legacy servers would crash under high load, causing lost bookings and severe customer dissatisfaction.",
      solution: "We orchestrated a complete migration to a serverless architecture. By decoupling their database and implementing auto-scaling microservices, the platform now handles massive traffic spikes automatically without downtime.",
      features: [
        "Serverless Microservices Migration",
        "Auto-Scaling Infrastructure",
        "Database Decoupling & Optimization",
        "High-Availability Load Balancing"
      ],
      techStack: ["AWS Lambda", "DynamoDB", "Next.js", "Vercel"]
    },
    {
      client: "Glinte LipGloss",
      industry: "E-Commerce",
      title: "D2C E-Commerce Growth Engine",
      tags: ["Client Work", "live"],
      summary: "How We Scaled Customer Support 3x and Recovered 25% of Abandoned Carts via Conversational AI.",
      heroMetrics: "25% Cart Recovery Rate, 3x Support Capacity, <30s Avg Response Time.",
      clientDescription: "Glinte LipGloss is a fast-growing D2C beauty brand with a massive social media following and high daily transaction volumes.",
      problem: "Their customer support team was overwhelmed by WISMO (Where Is My Order) inquiries, causing genuine product questions to get lost. Simultaneously, their checkout abandonment rate was soaring, and traditional email retargeting was seeing less than a 2% conversion rate.",
      solution: "We integrated a unified conversational AI engine on WhatsApp and their storefront. The AI handles 80% of tier-1 support queries instantly. We also implemented an automated cart recovery flow via WhatsApp that proactively reaches out to customers, resulting in massive conversion improvements.",
      features: [
        "WhatsApp Conversational AI Integration",
        "Automated WISMO Resolution",
        "Smart Cart Recovery Workflows",
        "Unified Omnichannel Support Dashboard"
      ],
      techStack: ["Shopify API", "WhatsApp Business API", "OpenAI", "Make.com"]
    },
    {
      client: "Fluxx Media",
      industry: "Media & Advertising",
      title: "Full-Stack Media Infrastructure & Content Automation",
      tags: ["Client Work", "live"],
      summary: "How We Built a Scalable Tech Infrastructure and Content Automation Engine to Save Hundreds of Hours on Client Deliverables.",
      heroMetrics: "60% Faster Content Turnaround, 40% Reduction in Ad Operations Overhead, 100% Automated Backend Reporting.",
      clientDescription: "Fluxx Media is a high-growth creative and marketing agency managing large-scale content production and ad campaigns for diverse clients.",
      problem: "Fluxx Media was struggling with fragmented backend systems and manual content workflows. Managing client ad accounts, tracking creative assets, and generating performance reports were all manual processes that bottlenecked their ability to scale and deliver results quickly.",
      solution: "We architected a robust tech infrastructure and a centralized backend management system. We implemented advanced content automation pipelines that handle repetitive creative tasks and integrated automated reporting tools for their ad operations, allowing the team to focus purely on high-level strategy and creativity.",
      features: [
        "Centralized Agency Backend Infrastructure",
        "Automated Creative Content Pipelines",
        "AI-Driven Ad Work Analytics",
        "Dynamic Client Reporting Dashboards"
      ],
      techStack: ["Node.js", "React", "Python", "Google Ads API", "Meta Graph API"]
    },
    {
      client: "Mr. Jain",
      industry: "Real Estate",
      title: "CRM + AI Revenue Automation Engine",
      tags: ["Client Work", "live"],
      summary: "How We Helped Mr. Jain Increase Revenue by 42% Without Hiring a Single New Sales Rep",
      heroMetrics: "42% Revenue Increase, <2 min Lead Response Time, 7.3x Avg Follow-up Touches, 31% Reduction in Site Visit No-shows.",
      clientDescription: "Mr. Jain manages a 6-person real estate sales team handling high-intent property investors and buyers.",
      problem: "Before our engagement, the team was bleeding revenue. Leads arrived from 5+ sources with no unified pipeline, no auto-assignment, and 3-5 hour response times. Follow-ups collapsed to just 2 touches per lead (industry average requires 6-8). Reps wasted time on unqualified browsers instead of high-intent buyers, and management had zero pipeline visibility.",
      solution: "We implemented a 5-Layer Automation Engine. We integrated all lead sources into one CRM with instant auto-assignment and a sub-2-minute WhatsApp/SMS response. We added AI Lead Scoring to prioritize hot leads, automated a 7-step follow-up sequence, introduced self-booking calendar links, and integrated AI call intelligence to detect objections and score deal probabilities.",
      features: [
        "Cross-Platform Lead Capture & Auto-assignment",
        "AI Lead Scoring & Prioritization",
        "Multi-Touch Follow-Up Automation",
        "Automated Site Visit & Calendar Booking",
        "AI Call Intelligence & Sales Analytics"
      ],
      techStack: ["Make.com", "OpenAI", "WhatsApp API", "GoHighLevel", "Twilio"]
    },
    {
      client: "Mr. Phillip",
      industry: "B2B Sales",
      title: "Full-Stack Sales Automation Engine",
      tags: ["Client Work", "live"],
      summary: "How We Turned Mr. Phillip's Broken Sales Machine into a Fully Automated Revenue Engine",
      heroMetrics: "<60s Lead Response Time, 6.4x Avg Follow-up Touches, 20 min Close-to-Payment Time, Post-Close Delay Eliminated.",
      clientDescription: "Mr. Phillip runs a B2B high-ticket sales operation, relying heavily on fast responses and complex onboarding workflows.",
      problem: "Leads from 5 platforms were manually entered into the CRM, averaging 4-6 hour response times. Sales reps only averaged 1.8 follow-ups and wasted time on freebie-seekers due to no lead scoring. After a verbal close, manual contracts and invoicing caused 24-48 hour delays, killing deal momentum.",
      solution: "We built a 5-Layer Automation Engine that captures leads from all sources with sub-60-second response times. AI scores the leads as HOT/WARM/COLD before calls. A smart multi-touch sequence handles follow-ups automatically. Finally, verbal agreements trigger instant contract generation, e-signatures, invoicing, and project board creation, cutting close-to-payment time to 20 minutes.",
      features: [
        "Sub-60s Cross-Platform Lead Capture",
        "AI Lead Scoring & Qualification",
        "Rule-Based Smart Follow-Up Sequences",
        "AI Conversation Intelligence",
        "Automated Proposal to Onboarding Flow"
      ],
      techStack: ["Zapier", "HubSpot", "PandaDoc", "Stripe", "OpenAI"]
    },
    {
      client: "Sarah Mitchell",
      industry: "Social Media Agency",
      title: "Cross-Platform Automation Engine",
      tags: ["Client Work", "live"],
      summary: "How We Helped Sarah Mitchell Scale Her Agency 40% Without Hiring 3 More People",
      heroMetrics: "+40% Agency Scale, 65% Less Execution Time, 12 min Avg Engagement Response, 75% Less Reporting Time.",
      clientDescription: "Sarah runs a Social Media Agency managing 18 retainer clients and producing 400+ posts per month across 5 platforms.",
      problem: "Every client required native posting on Instagram, TikTok, LinkedIn, Facebook, and Pinterest. DMs and comments were fragmented across platforms with 3-hour response times. Scaling was entirely linear: more clients meant more headcount and lower margins.",
      solution: "We deployed a 4-Layer Automation Engine. Content is uploaded once and auto-formats for each platform. A Unified Engagement Hub centralizes all DMs and comments with AI auto-responses for FAQs. A cross-platform analytics dashboard provides instant ROI reporting, cutting reporting time by 75%.",
      features: [
        "Cross-Platform Publishing Automation",
        "Unified Engagement Hub for DMs",
        "Cross-Platform Analytics Intelligence",
        "Workflow Automation for Production Scaling"
      ],
      techStack: ["Python", "Instagram Graph API", "TikTok API", "Make.com", "Airtable"]
    },
    {
      client: "Dr. James Clarke",
      industry: "Healthcare",
      title: "Clinical Workflow Automation",
      tags: ["Client Work", "live"],
      summary: "How Dr. James Clarke Reclaimed 2 Hours a Day and Increased Revenue by 21%",
      heroMetrics: "21% Revenue Increase, 2 hrs Time Reclaimed Daily, 88 min Admin Waste Eliminated, 12% Billing Leakage Plugged.",
      clientDescription: "Dr. Clarke runs a busy clinical practice seeing 22 patients a day, heavily burdened by administrative and documentation tasks.",
      problem: "Discharge summaries arrived as unstructured PDFs requiring multiple logins, wasting 88 minutes daily. Dr. Clarke spent 40% of each visit typing notes. 9-12 charts remained incomplete daily, forcing 2 hours of 'second shift medicine' at night. Rushed documentation caused coding inconsistencies and an estimated 8-12% revenue leakage.",
      solution: "We integrated clinical workflow automation. Discharge summaries and records were pulled into a single structured view. Ambient AI transcription during visits auto-populated notes and billing codes. Intelligent filtering removed alert fatigue, and real-time coding suggestions captured missed revenue and prevented claim denials.",
      features: [
        "Unified Medical Record Access",
        "AI-Assisted Ambient Documentation",
        "Intelligent Clinical Alert Filtering",
        "Automated Billing & Coding QA"
      ],
      techStack: ["HL7/FHIR APIs", "AWS HealthLake", "OpenAI Speech-to-Text", "Python"]
    },
    {
      client: "Vrindavan Greens",
      industry: "Hospitality",
      title: "AI Dynamic Pricing Engine",
      tags: ["Client Work", "live"],
      summary: "How We Increased Annual Revenue by 40% Using AI-Driven Dynamic Pricing Models.",
      heroMetrics: "40% Annual Revenue Increase, Optimized Off-Peak Occupancy, Real-Time Market Adaptation.",
      clientDescription: "Vrindavan Greens is a premium event and hospitality venue that struggled with seasonal revenue fluctuations.",
      problem: "The venue experienced low occupancy rates during off-peak seasons due to static pricing structures. Management lacked the data tools to adjust rates based on local demand, competitor pricing, and historical trends.",
      solution: "We implemented an AI-powered dynamic pricing engine that analyzes market signals, local event calendars, and historical booking data to automatically adjust pricing in real-time, maximizing both occupancy and revenue margins.",
      features: [
        "Real-Time Market Data Ingestion",
        "AI Dynamic Pricing Algorithms",
        "Automated Rate Distribution",
        "Occupancy Forecasting Dashboard"
      ],
      techStack: ["Python", "Scikit-Learn", "PostgreSQL", "Next.js"]
    },
    {
      client: "Aryan Advertising",
      industry: "Marketing",
      title: "Ad-Spend Optimization Engine",
      tags: ["Client Work", "live"],
      summary: "How We Reduced Advertising Waste by 35% with an AI-Driven Allocation Engine.",
      heroMetrics: "35% Reduction in Ad Waste, Cross-Channel Spend Optimization, Real-Time ROI Tracking.",
      clientDescription: "Aryan Advertising is a performance marketing agency managing millions in ad spend across fragmented digital channels.",
      problem: "The agency suffered from inefficient ad-spend allocation across fragmented channels. Media buyers manually adjusted budgets, resulting in slow reaction times to underperforming ads and wasted marketing dollars.",
      solution: "We developed an AI-driven allocation engine that plugs directly into Meta, Google, and TikTok ad platforms. The system autonomously shifts budgets from underperforming campaigns to high-ROI channels in real-time, drastically reducing wasted spend.",
      features: [
        "Cross-Platform Ad API Integrations",
        "Autonomous Budget Reallocation",
        "Real-Time Performance Telemetry",
        "Unified ROI Reporting Dashboard"
      ],
      techStack: ["Facebook Marketing API", "Google Ads API", "Python", "Airflow"]
    }
  ];

  return (
    <div className="min-h-screen bg-white text-black selection:bg-indigo-100 relative flex flex-col">
      <Navbar isLoggedIn={false} />

      <main className="pt-32 pb-16 flex-grow">
        <section className="px-6 max-w-4xl mx-auto mb-16 pt-8">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <p className="text-zinc-500 text-[11px] font-bold uppercase tracking-[0.2em] mb-6">
              Case Studies
            </p>
            <h1 className="text-3xl md:text-[2.75rem] font-semibold tracking-tight text-black leading-[1.15] mb-4">
              Our Portfolio
            </h1>
            <p className="text-base text-zinc-700 max-w-xl leading-[1.8]">
              Explore how we've helped businesses across industries turn operational chaos into automated revenue engines.
            </p>
          </motion.div>
        </section>

        <section className="px-6 max-w-5xl mx-auto mb-20">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {caseStudies.map((study, i) => (
              <motion.div
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                key={i}
                className="border border-zinc-200 rounded-lg p-5 flex flex-col group hover:border-indigo-200 transition-all cursor-pointer"
                onClick={() => setSelectedCaseStudy(study)}
              >
                <div className="flex items-center justify-between mb-4">
                  <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">{study.industry}</span>
                  <ArrowRight className="w-3.5 h-3.5 text-zinc-400 group-hover:text-indigo-600 transition-colors" />
                </div>
                <h3 className="text-sm font-semibold text-black mb-2 leading-snug">{study.client}: {study.title}</h3>
                <p className="text-zinc-600 text-xs leading-relaxed mb-4 flex-grow line-clamp-2">
                  {study.summary}
                </p>
                <div className="pt-3 border-t border-zinc-100">
                  <p className="text-black font-semibold text-[11px] leading-relaxed">
                    {study.heroMetrics.split(',').slice(0, 2).join(', ')}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="px-6 max-w-4xl mx-auto mb-16">
          <div className="border-t border-zinc-200 pt-14 text-center">
            <h2 className="text-xl md:text-2xl font-semibold tracking-tight text-black mb-3">
              Ready to make your business AI-native?
            </h2>
            <p className="text-zinc-600 text-sm max-w-md mx-auto mb-8">
              Start with a 30-minute strategy call. No pitch, no pressure.
            </p>
            <a
              href="/contact"
              className="inline-flex items-center gap-2 bg-indigo-600 text-white px-6 py-2.5 rounded-md text-sm font-medium hover:bg-indigo-700 transition-colors"
            >
              Book Your Free Strategy Call
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </section>
      </main>

      <Footer2 />

      {/* Case Study Modal */}
      <AnimatePresence>
        {selectedCaseStudy && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-white overflow-y-auto w-full h-full"
          >
            <div className="max-w-5xl mx-auto px-6 py-8 md:py-12">
              <button
                onClick={() => setSelectedCaseStudy(null)}
                className="text-zinc-600 hover:text-indigo-600 flex items-center gap-2 mb-8 text-xs font-medium uppercase tracking-wider transition-colors"
              >
                <ArrowLeft className="w-4 h-4" /> Back to Case Studies
              </button>

              <div className="flex gap-3 mb-6 items-center">
                <span className="bg-zinc-100 text-zinc-700 px-2.5 py-1 text-[10px] font-mono uppercase tracking-wider rounded">
                  {selectedCaseStudy.tags[0]}
                </span>
                <span className="text-black text-[10px] font-mono uppercase tracking-wider flex items-center gap-1.5 font-bold">
                  <div className="w-1.5 h-1.5 rounded-full bg-zinc-950"></div>
                  {selectedCaseStudy.tags[1]}
                </span>
              </div>

              <h1 className="text-2xl md:text-4xl font-semibold tracking-tight text-black mb-8 leading-[1.1]">
                {selectedCaseStudy.title}
              </h1>

              <div className="border-l-2 border-black pl-4 py-1 mb-12 max-w-4xl">
                <p className="text-base md:text-xl text-zinc-900 leading-relaxed font-medium">
                  {selectedCaseStudy.heroMetrics}
                </p>
              </div>

              <div className="grid md:grid-cols-[1fr_2fr] gap-8 md:gap-16">
                {/* Left Column */}
                <div>
                  <div className="bg-zinc-100 aspect-video rounded-lg mb-8 flex items-center justify-center text-zinc-400">
                    <ImageIcon className="w-12 h-12 opacity-50" />
                  </div>
                  <h3 className="text-lg font-bold text-black mb-2">{selectedCaseStudy.client}</h3>
                  <p className="text-zinc-700 text-sm leading-relaxed">{selectedCaseStudy.clientDescription}</p>
                </div>

                {/* Right Column */}
                <div className="space-y-12">
                  <div>
                    <span className="text-[10px] font-bold uppercase tracking-widest text-black mb-4 block">The Problem</span>
                    <p className="text-zinc-800 text-sm leading-relaxed">{selectedCaseStudy.problem}</p>
                  </div>

                  <div>
                    <span className="text-[10px] font-bold uppercase tracking-widest text-black mb-4 block">What We Built</span>
                    <p className="text-zinc-800 text-sm leading-relaxed">{selectedCaseStudy.solution}</p>
                  </div>

                  <div>
                    <span className="text-[10px] font-bold uppercase tracking-widest text-black mb-4 block">Features</span>
                    <ul className="space-y-3">
                      {selectedCaseStudy.features.map((f: string, i: number) => (
                        <li key={i} className="flex items-start gap-3 text-zinc-800 text-sm">
                          <Check className="w-5 h-5 text-black shrink-0" />
                          <span className="leading-relaxed">{f}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <span className="text-[10px] font-bold uppercase tracking-widest text-black mb-4 block">Tech Stack</span>
                    <div className="flex flex-wrap gap-2">
                      {selectedCaseStudy.techStack.map((t: string, i: number) => (
                        <span key={i} className="bg-zinc-100 text-zinc-700 px-3 py-1.5 text-[10px] font-mono rounded">
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Bottom Close Button */}
              <div className="mt-16 pt-8 border-t border-zinc-200 text-center">
                <button
                  onClick={() => setSelectedCaseStudy(null)}
                  className="inline-flex items-center gap-2 bg-indigo-600 text-white px-6 py-2.5 rounded-md text-sm font-medium hover:bg-indigo-700 transition-colors"
                >
                  Back to Case Studies
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
